/**
 * Wie ein Objekt in einer Auswahlliste heisst.
 *
 * Die Anschrift ist der falsche Merkposten. Ein Mitarbeiter, der abends einen
 * Beleg eintraegt, weiss, dass er bei Hornburg war; ob das Haus in der Carrer
 * Peru 106 oder 107 steht, weiss er nicht, und in einer Liste aus vierzig
 * Anschriften sucht er es auch nicht. Ein Name ist leichter zu merken als eine
 * Adresse, und das ist keine Geschmacksfrage, sondern der Unterschied zwischen
 * einem Beleg am richtigen Objekt und einem am falschen.
 *
 * Deshalb steht hier der Kundenname vorn. Die Bezeichnung des Objekts kommt
 * nur dazu, wenn derselbe Kunde mehrere Objekte hat, denn nur dann sagt sie
 * etwas. Bei einem Eigentuemer mit einem Haus waere sie eine zweite Zeile, die
 * jedes Mal gelesen und jedes Mal verworfen wird.
 *
 * Eine Stelle fuer alle Listen: Kosten, Tankbuch, Zeiten, Dienstplan,
 * Auftraege, Pruefungen. Vorher stand die Regel fuenfmal ausgeschrieben in den
 * Masken, dreimal gar nicht, und deshalb hiess dasselbe Objekt je nach Seite
 * anders.
 */

export interface ObjektKurz {
  id: string;
  name: string;
  customer_name?: string | null;
}

/** Die Beschriftung eines einzelnen Objekts, wenn die Liste bekannt ist. */
function beschriften(objekt: ObjektKurz, mehrfach: boolean): string {
  const kunde = (objekt.customer_name ?? "").trim();
  // Ohne Kundennamen bleibt nur die Bezeichnung. Das kommt bei uebernommenen
  // Datensaetzen vor und ist besser als ein fuehrendes Trennzeichen.
  if (!kunde) return objekt.name;
  return mehrfach ? `${kunde} · ${objekt.name}` : kunde;
}

/**
 * Die Objekte in der Reihenfolge, in der sie in einer Auswahl stehen sollen.
 *
 * Sortiert wird nach der Beschriftung und nicht nach der Bezeichnung des
 * Objekts. Sonst stuenden die Kundennamen in der Reihenfolge der Anschriften,
 * und eine Liste, die alphabetisch aussieht, es aber nicht ist, ist schlimmer
 * als eine unsortierte.
 */
export function objektAuswahl<T extends ObjektKurz>(
  objekte: T[]
): { objekt: T; beschriftung: string }[] {
  const jeKunde = new Map<string, number>();
  for (const o of objekte) {
    const kunde = (o.customer_name ?? "").trim();
    if (kunde) jeKunde.set(kunde, (jeKunde.get(kunde) ?? 0) + 1);
  }

  return objekte
    .map((objekt) => ({
      objekt,
      beschriftung: beschriften(objekt, (jeKunde.get((objekt.customer_name ?? "").trim()) ?? 0) > 1)
    }))
    .sort((a, b) => a.beschriftung.localeCompare(b.beschriftung));
}

/**
 * Die Beschriftung eines Objekts, das nicht aus einer Liste kommt.
 *
 * Etwa in der Ueberschrift eines Auftrags. Ob der Kunde weitere Objekte hat,
 * ist dort nicht bekannt, deshalb steht beides da.
 */
export function objektMitKunde(objekt: ObjektKurz): string {
  const kunde = (objekt.customer_name ?? "").trim();
  return kunde ? `${kunde} · ${objekt.name}` : objekt.name;
}
